import { Component, EventEmitter, Input, Output, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  PaymentTerminalDto,
  TerminalDeviceAdminService,
  TerminalDeviceCredentialDto
} from '../../../../../api/gen';
import { environment } from '../../../../../environments/environment';
import { TerminalCoinAcceptorComponent } from './terminal-coin-acceptor/terminal-coin-acceptor.component';

/** View shape for the ZVT driver fields not yet in the generated PaymentTerminalConfigDto (see the form component note). */
interface PaymentTerminalConfigView {
  driverKind?: string;
  zvtHost?: string | null;
  zvtPort?: number | null;
}

@Component({
  selector: 'app-zone-payment-terminal',
  standalone: true,
  imports: [
    CommonModule, FormsModule, MatCardModule, MatButtonModule, MatIconModule,
    MatTooltipModule, MatFormFieldModule, MatInputModule, MatProgressSpinnerModule,
    TerminalCoinAcceptorComponent
  ],
  templateUrl: './zone-payment-terminal.component.html',
  styleUrl: './zone-payment-terminal.component.scss'
})
export class ZonePaymentTerminalComponent {
  private snackBar = inject(MatSnackBar);
  private terminalDeviceApi = inject(TerminalDeviceAdminService);

  @Input() terminal!: PaymentTerminalDto;
  @Input() isProcessing = false;
  @Output() deleteTerminal = new EventEmitter<string>();
  @Output() editTerminal = new EventEmitter<PaymentTerminalDto>();

  get driverKind(): string | undefined {
    return (this.terminal?.config as PaymentTerminalConfigView | undefined)?.driverKind;
  }

  /**
   * Whether the column is alive right now, from the heartbeat timestamp rather than the stored
   * DeviceStatus. Those two can disagree, and when they do the timestamp is the one to trust: a
   * status is a transition someone has to have written, so a backend host that dies without running
   * OnDisconnectedAsync leaves the row reading Online forever. Medicent's terminal read Offline all
   * day while taking live card payments (2026-08-26) because nothing wrote the column at all.
   */
  get isOnline(): boolean {
    return this.terminal?.isOnline === true;
  }

  get lastHeartbeatAt(): string | null {
    return this.terminal?.lastHeartbeatAt ?? null;
  }

  /**
   * When a command last provably **reached** this column — a probe pushed down its SignalR group and
   * echoed back by the Pi, which is the exact route a charge takes.
   *
   * Shown beside the heartbeat, never instead of it, because conflating the two is what made the
   * 2026-09-02 outage invisible. Both clients at a column share the group, and the kiosk browser
   * beats every 60 seconds — so Medicent read Online for sixteen hours while eighteen charges were
   * pushed into a connection the Pi could no longer receive on. Three drivers left without paying and
   * this card said the terminal was fine.
   */
  get lastCommandAckAt(): string | null {
    return this.terminal?.lastCommandAckAt ?? null;
  }

  /**
   * True when the column is answering us but not receiving commands — the shape of the outage.
   *
   * Null is deliberately NOT unreachable: an older station or an older daemon simply never sends the
   * probe, and painting those as broken would train operators to ignore this the way they learned to
   * ignore DeviceStatus. Silence about an unknown beats a confident wrong answer.
   */
  get isUnreachableForCommands(): boolean {
    if (!this.isOnline || !this.lastCommandAckAt) return false;

    const stale = Date.now() - new Date(this.lastCommandAckAt).getTime();
    return stale > ZonePaymentTerminalComponent.COMMAND_ACK_STALE_MS;
  }

  /**
   * Set against how often the stamp is WRITTEN, not how often the probe runs.
   *
   * The Pi probes every 15 s, but the backend throttles the row update through DeviceHeartbeatCache
   * (PersistInterval, 1 minute), so a healthy column's stamp is routinely ~60-75 s old in steady
   * state. Two minutes survived that, with about 45 s to spare.
   *
   * The margin goes when the Pi REBUILDS, which is the moment someone is most likely to be looking
   * at this badge: the probe timeout is 45 s and reconnecting costs a few more, and that stacks on
   * top of a 60 s persist cycle — roughly 110 s, brushing a 120 s threshold. The column is fine and
   * repairing itself, and the badge would call it unreachable. A badge that cries wolf during normal
   * self-healing is one operators learn to ignore, which is how DeviceStatus became useless here.
   *
   * Three minutes clears the rebuild case with room left. The badge is a secondary signal anyway —
   * the Pi recovers unattended in ~45 s — so this is for the outage that SURVIVES the rebuild.
   *
   * If PersistInterval ever moves, this must move with it.
   */
  private static readonly COMMAND_ACK_STALE_MS = 3 * 60 * 1000;

  get zvtEndpoint(): string | null {
    const cfg = this.terminal?.config as PaymentTerminalConfigView | undefined;
    if (!cfg?.zvtHost) return null;
    return cfg.zvtPort ? `${cfg.zvtHost}:${cfg.zvtPort}` : cfg.zvtHost;
  }

  /**
   * The per-station handle the payment app resolves at runtime (from its launch URL / localStorage) — NOT a
   * build-time env value, because the app is one static site in Azure shared by every station. Point each
   * station's kiosk browser at `<payment-app-url>?terminalId=<this id>`.
   */
  get stationUrlParam(): string {
    return this.terminal?.id ? `?terminalId=${this.terminal.id}` : '';
  }

  /**
   * The full kiosk URL to point this station's browser at — the payment app for THIS environment
   * (environment.paymentUrl) plus the terminal handle. Copying only the `?terminalId=…` fragment left
   * whoever provisions the station to guess the host, and a station aimed at the wrong environment's
   * payment app looks perfectly normal until a card is presented.
   */
  get stationUrl(): string {
    if (!this.terminal?.id) return '';
    return `${environment.paymentUrl.replace(/\/+$/, '')}/${this.stationUrlParam}`;
  }

  // ── Signing key ────────────────────────────────────────────────────────────
  //
  // The Pi generates its own Ed25519 keypair on first boot and keeps the private half; only the
  // public half is registered here. Until it is, `POST /public/payment/terminal-capture/outcome`
  // rejects the station with 401 — fail-closed by design. That failure is invisible from the lane:
  // the card is charged by the reader and the backend simply never learns it, so the stay stays
  // unpaid and the barrier stays down. Registering the key is therefore part of commissioning a
  // column, not an optional extra, and it is redone after every reflash (a new SD card means a new
  // keypair).
  //
  // Deliberately NOT showing a "no key registered" state: the backend exposes only the PUT, so we
  // cannot read the current credential and would be guessing. An empty panel that says nothing beats
  // a confident "not registered" that sends someone to re-provision a healthy column.

  readonly signingKeyInput = signal('');
  readonly isRegisteringKey = signal(false);

  /** Set from the PUT response, so what is shown is what the backend actually stored. */
  readonly registeredCredential = signal<TerminalDeviceCredentialDto | null>(null);

  registerSigningKey(): void {
    const publicKey = this.signingKeyInput().trim();
    const terminalId = this.terminal?.id;
    if (!publicKey || !terminalId || this.isRegisteringKey()) return;

    this.isRegisteringKey.set(true);
    // Algorithm is omitted, not sent as a literal: the backend defaults it to Ed25519, the only
    // algorithm it supports, and a hard-coded string here would have to be changed in two places
    // the day a second one is added.
    this.terminalDeviceApi
      .paymentTerminalDeviceTerminalIdSigningKeyPut(terminalId, { publicKey })
      .subscribe({
        next: (credential) => {
          this.registeredCredential.set(credential);
          this.signingKeyInput.set('');
          this.isRegisteringKey.set(false);
          this.snackBar.open('Signing key registered', 'Close', { duration: 2500 });
        },
        error: (err) => {
          this.isRegisteringKey.set(false);
          // MadResult errors come back as a PLAIN STRING body ("Title.Detail" — see
          // HttpStatusExtension.ToActionResult), not a problem-details object, so err.error is the
          // message itself. 403 and 500 carry no body at all, hence the fallback: showing the
          // reader why a key was rejected ("must be a 32-byte Ed25519 key") is the whole point of
          // surfacing this here rather than leaving it in a curl response.
          const body = typeof err?.error === 'string' ? err.error.trim() : '';
          this.snackBar.open(
            body || 'Could not register the signing key',
            'Close',
            { duration: 5000 }
          );
        }
      });
  }

  copy(value: string | undefined | null, label: string): void {
    if (!value) return;
    navigator.clipboard?.writeText(value).then(
      () => this.snackBar.open(`${label} copied`, 'Close', { duration: 1500 }),
      () => this.snackBar.open(`Could not copy ${label}`, 'Close', { duration: 2000 })
    );
  }

  onEdit(): void {
    if (this.terminal) {
      this.editTerminal.emit(this.terminal);
    }
  }

  onDelete(): void {
    if (this.terminal?.id) {
      this.deleteTerminal.emit(this.terminal.id);
    }
  }
}
